import { useCalculateUserRisk } from "#/services/kyan/hooks/useCalculateUserRisk";
import { SuccessfulUserPortfolioResponse } from "#/services/kyan/kyan.types";
import { useComboPrices } from "#/store/combo/comboPrices";
import { useAccountState } from "#/store/wallet/account/accountState";
import { type CompactAccountState } from "#/types/accounts";
import { ComboPricesRowType, type PricesTableEntry } from "#/types/combo";
import { getMmrImr } from "#/utils/getMmrImr";
import { isBuy } from "#/utils/marketActionConverter";

export const useCalculateUserRiskForCombo = () => {
  const { comboPrices: comboPricesFromWorker, hasEnoughLiquidity } =
    useComboPrices();
  const { portfolioState } = useAccountState();

  const payload = comboPricesFromWorker
    .filter(
      (comboPriceEntry) =>
        comboPriceEntry.rowType !== ComboPricesRowType.Summary,
    )
    .map((comboPriceEntry) => ({
      instrument: comboPriceEntry.instrument,
      size: comboPriceEntry.size!.toString(),
      isBuy: isBuy(comboPriceEntry.side!),
      isEnabled: hasEnoughLiquidity,
    }));
  const userRisk = useCalculateUserRisk({
    inputs: payload,
    refetchInterval: 30_000,
    option: null,
  });
  const { imr, mmr } = getMmrImr({
    equity: portfolioState.equity,
    im: userRisk.im,
    mm: userRisk.mm,
  });
  const hasSufficientMargin = imr < 1;

  return {
    imr,
    mmr,
    userRisk,
    portfolioState,
    hasEnoughLiquidity,
    hasSufficientMargin,
    comboPricesFromWorker,
  };
};

export const useCalculateUserRiskForComboOrderbook = () => {
  const {
    imr,
    mmr,
    userRisk,
    portfolioState,
    hasEnoughLiquidity,
    hasSufficientMargin,
    comboPricesFromWorker,
  } = useCalculateUserRiskForCombo();
  const { limitNetPrices } = useComboPrices();

  const comboPricesWithGreeks = updateComboPricesWithGreeks({
    comboPrices: comboPricesFromWorker,
    portfolioState,
    userRisk,
    imr,
    mmr,
  });

  const comboPrices = updateWithLimitNetPrices(
    comboPricesWithGreeks,
    limitNetPrices,
  );

  return {
    comboPrices,
    hasEnoughLiquidity,
    hasSufficientMargin,
    isLoading: userRisk.isLoading,
  };
};

export const updateComboPricesWithGreeks = ({
  comboPrices,
  portfolioState,
  userRisk,
  imr,
  mmr,
}: {
  comboPrices: PricesTableEntry[];
  portfolioState: CompactAccountState;
  userRisk: SuccessfulUserPortfolioResponse;
  imr: number;
  mmr: number;
}) => {
  return [
    ...comboPrices,
    {
      instrument: "Total Summary",
      rowType: ComboPricesRowType.TotalSummary,
      imr: { from: portfolioState.imr, to: imr },
      mmr: { from: portfolioState.mmr, to: mmr },
      delta: {
        from: portfolioState.greeks.delta,
        to: userRisk.greeks.delta,
      },
      gamma: {
        from: portfolioState.greeks.gamma,
        to: userRisk.greeks.gamma,
      },
      vega: {
        from: portfolioState.greeks.vega,
        to: userRisk.greeks.vega,
      },
      theta: {
        from: portfolioState.greeks.theta,
        to: userRisk.greeks.theta,
      },
    } as PricesTableEntry,
  ];
};

export const updateWithLimitNetPrices = (
  comboPrices: PricesTableEntry[],
  limitNetPrices: Partial<Record<ComboPricesRowType, string>>,
) => {
  return comboPrices.map((comboPrice) => ({
    ...comboPrice,
    limitNetPrice: limitNetPrices[comboPrice.rowType],
  }));
};
